<template>
<div class="resource-page">
  <HeadNav selectedLanguage="en" page="free-proxy-list" />

  <main>
    <section class="resource-hero">
      <div class="resource-hero__content">
        <p class="eyebrow">Proxy Protocols</p>
        <h1>Understanding Free Proxies & Protocols (HTTP, HTTPS, SOCKS5)</h1>
        <p>
          Public proxies offer flexible network routing for developers, network engineers, and privacy-conscious users.
          Explore how HTTP, HTTPS, SOCKS4, and SOCKS5 protocols work across global locations to support safe data
          extraction, network diagnostics, and general browsing.
        </p>
      </div>
    </section>

    <section class="content-section">
      <h2>Proxy Protocol Overview</h2>
      <ul>
        <li><strong>4 Network Protocols Supported:</strong> HTTP, HTTPS, SOCKS4, SOCKS5</li>
        <li><strong>Global Infrastructure:</strong> Distributed routing across 10+ countries</li>
        <li><strong>Automated Verification:</strong> Regular connectivity checks for active endpoints</li>
      </ul>
    </section>

    <section class="content-section">
      <h2>What is a Proxy and How Does It Work?</h2>
      <p>A proxy server acts as an intermediary gateway between your local device and the target server. When you send a web request through a proxy:</p>
      <ul>
        <li><strong>Traffic Routing:</strong> Your web requests are routed through the proxy server's network location.</li>
        <li><strong>IP Privacy:</strong> The destination server sees the proxy server's IP address rather than your local home or office network identity.</li>
        <li><strong>Developer Flexibility:</strong> Enables developers, automated QA scripts, and data scrapers to run tests and collect public information cleanly.</li>
      </ul>
    </section>

    <section class="content-section">
      <h2>Comparing Proxy Protocols: HTTP vs. HTTPS vs. SOCKS4 vs. SOCKS5</h2>
      <p>Choosing the right network protocol determines how effectively your traffic is processed:</p>
      <ul>
        <li><strong>HTTP Proxies:</strong> Designed for standard, unencrypted web traffic. Best suited for basic web crawling and public data extraction.</li>
        <li><strong>HTTPS (SSL) Proxies:</strong> Adds an encrypted SSL/TLS tunnel layer. Ideal for secure transmissions and protecting payloads in transit.</li>
        <li><strong>SOCKS4 Proxies:</strong> A general-purpose protocol built for TCP traffic. Supports basic network requests without native authentication.</li>
        <li><strong>SOCKS5 Proxies:</strong> The most advanced and versatile proxy protocol. SOCKS5 supports both TCP and UDP traffic, DNS resolution, and authentication—making it optimal for multi-threaded applications and developer automation.</li>
      </ul>
    </section>

    <section class="content-section">
      <h2>Proxy Anonymity Levels Explained</h2>
      <p>Proxy servers differ based on how they process HTTP header information (such as <code>HTTP_X_FORWARDED_FOR</code>):</p>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Anonymity Level</th>
              <th>Header Forwarding</th>
              <th>Destination Sees Proxy?</th>
              <th>Primary Use Case</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>High Anonymity (Elite)</td>
              <td>No Header Forwarding</td>
              <td>No</td>
              <td>Web automation, API testing, strict privacy tasks</td>
            </tr>
            <tr>
              <td>Anonymous</td>
              <td>Standard Forwarding</td>
              <td>Yes</td>
              <td>General Web browsing, network testing</td>
            </tr>
            <tr>
              <td>Transparent</td>
              <td>Full IP Forwarding</td>
              <td>Yes</td>
              <td>Network caching, bandwidth management</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="content-section cta-section">
      <h2>Looking for Full Device Security?</h2>
      <p>
        While public proxies handle specific app or browser traffic, they often lack full-device encryption and connection stability.
      </p>
      <p>
        If you require <strong>system-wide encryption, fast servers, and zero-log privacy</strong>, download the
        <strong>Secure VPN App</strong> for complete online protection.
      </p>
      <a
        class="vpn-btn vpn-btn--primary"
        href="https://play.google.com/store/apps/details?id=com.secure.vpn.private.browser&referrer=utm_source%3Dweb"
        target="_blank"
        rel="noopener noreferrer"
      >Get Secure VPN App</a>
    </section>

    <section class="content-section faq-section">
      <h2>Frequently Asked Questions</h2>

      <details>
        <summary>Are public proxies fully safe for private data?</summary>
        <p>
          Public proxies are generally best for non-sensitive tasks like API testing or public web scraping. For personal
          accounts, online banking, or confidential browsing, an encrypted VPN service is strongly recommended.
        </p>
      </details>

      <details>
        <summary>What is the difference between a Proxy and a VPN?</summary>
        <p>
          A proxy typically works at the application level (e.g., inside a single browser) and may not encrypt your data.
          A VPN encrypts <strong>all network traffic</strong> across your entire device, providing a safer and more stable connection.
        </p>
      </details>

      <details>
        <summary>Which protocol is best for software automation?</summary>
        <p>
          SOCKS5 is widely considered the best protocol for software automation due to its native support for TCP/UDP traffic and DNS routing.
        </p>
      </details>
    </section>
  </main>
</div>
</template>

<script>
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
  name: 'FreeProxyList',
  components: {
    HeadNav,
  },
};
</script>

<style lang="scss" scoped>
.resource-page {
  min-height: 70vh;
  background: #f7f5ff;
  color: var(--vpn-ink, #1f2430);
}

main {
  width: var(--vpn-content-width);
  margin: 0 auto;
  padding: 48px 0 72px;
}

.resource-hero,
.content-section {
  margin-top: 28px;
  padding: 30px;
  border: 1px solid var(--vpn-line, #e8e4f8);
  border-radius: 8px;
  background: #fff;
  box-shadow: var(--vpn-shadow, 0 12px 40px rgba(109, 74, 255, 0.12));
}

.resource-hero {
  margin-top: 0;
}

.eyebrow {
  margin: 0 0 10px;
  color: #198754;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: 46px;
  line-height: 1.1;
  font-weight: 850;
}

h2 {
  margin: 0 0 14px;
  font-size: 28px;
  line-height: 1.25;
}

p,
li {
  color: var(--vpn-ink-muted, #5b6475);
  font-size: 16px;
  line-height: 1.75;
}

ul {
  margin: 0;
  padding-left: 22px;
}

li + li {
  margin-top: 8px;
}

strong {
  color: var(--vpn-ink, #1f2430);
}

code {
  padding: 2px 5px;
  border-radius: 4px;
  background: #f3f6fb;
  color: #1f2430;
}

.table-wrap {
  margin-top: 20px;
  overflow-x: auto;
  border: 1px solid #dfe7f1;
  border-radius: 8px;
}

table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

th,
td {
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f5;
  text-align: left;
  vertical-align: top;
  font-size: 15px;
}

th {
  background: #f3f6fb;
  color: var(--vpn-ink, #1f2430);
  font-weight: 800;
}

td {
  color: var(--vpn-ink-muted, #5b6475);
}

tbody tr:last-child td {
  border-bottom: 0;
}

.cta-section {
  text-align: center;
}

.vpn-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 10px 24px;
  border-radius: 999px;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
}

.vpn-btn--primary {
  background: var(--vpn-brand, #6d4aff);
  box-shadow: 0 10px 24px rgba(109, 74, 255, 0.35);
}

.vpn-btn--primary:hover {
  background: var(--vpn-brand-deep, #5b3fe8);
}

details {
  border: 1px solid #e6edf5;
  border-radius: 8px;
  background: #fbfcff;
}

details + details {
  margin-top: 12px;
}

summary {
  cursor: pointer;
  padding: 16px 18px;
  color: var(--vpn-ink, #1f2430);
  font-weight: 800;
}

details p {
  margin: 0;
  padding: 0 18px 18px;
}

@media (max-width: 760px) {
  main {
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
    box-sizing: border-box;
    padding: 28px 0 48px;
  }

  .resource-hero,
  .content-section {
    padding: 22px;
  }

  h1 {
    font-size: 34px;
  }

  h2 {
    font-size: 24px;
  }
}
</style>
